import {
  Card,
  RANK_VALUE,
  RANK_NAMES,
  SUIT_SYMBOLS,
  isSameCard,
  createDeck,
} from "./Deck";

// Hand rankings from worst to best
export enum HandRank {
  HighCard = 0,
  Pair = 1,
  TwoPair = 2,
  ThreeOfAKind = 3,
  Straight = 4,
  Flush = 5,
  FullHouse = 6,
  FourOfAKind = 7,
  StraightFlush = 8,
  RoyalFlush = 9,
}

export const HAND_NAMES: Record<HandRank, string> = {
  [HandRank.HighCard]: "High Card",
  [HandRank.Pair]: "Pair",
  [HandRank.TwoPair]: "Two Pair",
  [HandRank.ThreeOfAKind]: "Three of a Kind",
  [HandRank.Straight]: "Straight",
  [HandRank.Flush]: "Flush",
  [HandRank.FullHouse]: "Full House",
  [HandRank.FourOfAKind]: "Four of a Kind",
  [HandRank.StraightFlush]: "Straight Flush",
  [HandRank.RoyalFlush]: "Royal Flush",
};

export interface HandResult {
  rank: HandRank;
  kickers: number[];
  name: string;
}

// --- Hand Evaluation ---

export function evaluateHand(cards: Card[]): HandResult {
  if (cards.length < 5) throw new Error("Need at least 5 cards");
  const combos = getCombinations(cards, 5);
  let best: HandResult | null = null;
  for (const combo of combos) {
    const result = evaluate5(combo);
    if (!best || compareHands(result, best) > 0) best = result;
  }
  return best!;
}

function evaluate5(cards: Card[]): HandResult {
  const values = cards.map((c) => RANK_VALUE[c.rank]).sort((a, b) => b - a);
  const suits = cards.map((c) => c.suit);
  const isFlush = suits.every((s) => s === suits[0]);
  const isStraight = checkStraight(values);
  const isWheel =
    values[0] === 14 &&
    values[1] === 5 &&
    values[2] === 4 &&
    values[3] === 3 &&
    values[4] === 2;

  if (isFlush && isStraight) {
    if (values[0] === 14 && values[1] === 13)
      return {
        rank: HandRank.RoyalFlush,
        kickers: values,
        name: HAND_NAMES[HandRank.RoyalFlush],
      };
    return {
      rank: HandRank.StraightFlush,
      kickers: isWheel ? [5, 4, 3, 2, 1] : values,
      name: HAND_NAMES[HandRank.StraightFlush],
    };
  }

  const groups = getGroups(values);

  if (groups[0].count === 4)
    return {
      rank: HandRank.FourOfAKind,
      kickers: [groups[0].value, groups[1].value],
      name: HAND_NAMES[HandRank.FourOfAKind],
    };
  if (groups[0].count === 3 && groups[1].count === 2)
    return {
      rank: HandRank.FullHouse,
      kickers: [groups[0].value, groups[1].value],
      name: HAND_NAMES[HandRank.FullHouse],
    };
  if (isFlush)
    return {
      rank: HandRank.Flush,
      kickers: values,
      name: HAND_NAMES[HandRank.Flush],
    };
  if (isStraight)
    return {
      rank: HandRank.Straight,
      kickers: isWheel ? [5, 4, 3, 2, 1] : values,
      name: HAND_NAMES[HandRank.Straight],
    };
  if (groups[0].count === 3)
    return {
      rank: HandRank.ThreeOfAKind,
      kickers: [groups[0].value, groups[1].value, groups[2].value],
      name: HAND_NAMES[HandRank.ThreeOfAKind],
    };
  if (groups[0].count === 2 && groups[1].count === 2)
    return {
      rank: HandRank.TwoPair,
      kickers: [groups[0].value, groups[1].value, groups[2].value],
      name: HAND_NAMES[HandRank.TwoPair],
    };
  if (groups[0].count === 2)
    return {
      rank: HandRank.Pair,
      kickers: groups.map((g) => g.value),
      name: HAND_NAMES[HandRank.Pair],
    };
  return {
    rank: HandRank.HighCard,
    kickers: values,
    name: HAND_NAMES[HandRank.HighCard],
  };
}

function checkStraight(sortedDesc: number[]): boolean {
  for (let i = 0; i < sortedDesc.length - 1; i++) {
    if (sortedDesc[i] - sortedDesc[i + 1] !== 1) {
      if (i === 0 && sortedDesc[0] === 14 && sortedDesc[1] === 5) continue;
      return false;
    }
  }
  return true;
}

interface Group {
  value: number;
  count: number;
}

function getGroups(values: number[]): Group[] {
  const counts: Record<number, number> = {};
  for (const v of values) counts[v] = (counts[v] || 0) + 1;
  return Object.entries(counts)
    .map(([v, c]) => ({ value: Number(v), count: c }))
    .sort((a, b) => b.count - a.count || b.value - a.value);
}

export function compareHands(a: HandResult, b: HandResult): number {
  if (a.rank !== b.rank) return a.rank - b.rank;
  for (let i = 0; i < Math.min(a.kickers.length, b.kickers.length); i++) {
    if (a.kickers[i] !== b.kickers[i]) return a.kickers[i] - b.kickers[i];
  }
  return 0;
}

function getCombinations<T>(arr: T[], k: number): T[][] {
  const result: T[][] = [];
  function backtrack(start: number, current: T[]) {
    if (current.length === k) {
      result.push([...current]);
      return;
    }
    for (let i = start; i < arr.length; i++) {
      current.push(arr[i]);
      backtrack(i + 1, current);
      current.pop();
    }
  }
  backtrack(0, []);
  return result;
}

// --- Outs Calculation ---

export interface OutGroup {
  /** The hand improvement name, e.g. "Flush" */
  improvementName: string;
  /** The hand rank of the improvement */
  improvementRank: HandRank;
  /** Cards that achieve this improvement */
  cards: Card[];
  /** Number of outs */
  count: number;
  /** Hit probability for this group */
  hitPercent: number;
}

/** A runner-runner improvement that requires both cards to come together */
export interface RunnerRunnerGroup {
  improvementName: string;
  improvementRank: HandRank;
  /** The specific two-card combos that achieve this improvement */
  combos: [Card, Card][];
  /** Number of hitting combos */
  comboCount: number;
  /** Total possible combos C(remaining, 2) */
  totalCombos: number;
  /** Exact probability */
  hitPercent: number;
}

export interface OutsResult {
  /** Current best hand */
  currentHand: HandResult;
  /** All out groups (single-card outs), sorted best improvement first */
  outGroups: OutGroup[];
  /** Runner-runner groups (need both cards), sorted best improvement first */
  runnerRunnerGroups: RunnerRunnerGroup[];
  /** Total unique single-card outs */
  totalOuts: number;
  /** Cards remaining in deck */
  remainingCount: number;
  /** Cards to come (1 or 2) */
  cardsToCome: number;
  /** Exact probability of hitting ANY improving single-card out, 0–100 */
  hitAnyPercent: number;
  /** Exact probability of ANY improvement including runner-runner, 0–100 (flop only, equals hitAnyPercent on turn) */
  hitAnyIncludingRRPercent: number;
  /** Total improving 2-card combos (flop only) */
  totalImprovingCombos: number;
  /** Total possible 2-card combos C(remaining,2) (flop only, 0 on turn) */
  totalPossibleCombos: number;
}

/**
 * Check if a hand improvement actually involves at least one hole card.
 * A "board pair" (e.g. two 10s on the board) doesn't help you specifically
 * because every player has it.
 */
function improvementUsesHoleCards(
  holeCards: Card[],
  communityCards: Card[],
  newCard: Card,
  newHand: HandResult,
  currentHand: HandResult
): boolean {
  // Evaluate what the board alone (community + new card) would give
  const boardCards = [...communityCards, newCard];
  // We can't evaluate fewer than 5 cards, so for 4 board cards we can't check.
  // For 5 board cards (turn + new card), we can compare directly.
  if (boardCards.length >= 5) {
    const boardHand = evaluateHand(boardCards);
    // If the board alone achieves the same rank or better, this out doesn't
    // help you specifically — everyone would have it
    if (boardHand.rank >= newHand.rank) {
      return false;
    }
  }

  // For flop (3 community + new card = 4 board cards), we can't evaluate
  // the board alone. Instead, check if the improvement involves hole card values.
  if (boardCards.length < 5) {
    const holeValues = holeCards.map((c) => RANK_VALUE[c.rank]);
    const holeSuits = holeCards.map((c) => c.suit);
    const boardValues = boardCards.map((c) => RANK_VALUE[c.rank]);

    // For pair/two-pair/trips/quads improvements: the paired value must
    // include at least one hole card rank
    if (
      newHand.rank === HandRank.Pair ||
      newHand.rank === HandRank.TwoPair ||
      newHand.rank === HandRank.ThreeOfAKind ||
      newHand.rank === HandRank.FourOfAKind ||
      newHand.rank === HandRank.FullHouse
    ) {
      // Count how many of each rank appear on the board vs with hole cards
      const boardCounts: Record<number, number> = {};
      for (const v of boardValues) boardCounts[v] = (boardCounts[v] || 0) + 1;

      const allValues = [...holeValues, ...boardValues];
      const allCounts: Record<number, number> = {};
      for (const v of allValues) allCounts[v] = (allCounts[v] || 0) + 1;

      // Find which ranks form groups (pairs/trips/etc) in the full hand
      // but NOT in the board alone
      let holeCardContributes = false;
      for (const v of holeValues) {
        // This hole card value appears more in the full set than on the board
        // meaning the hole card itself is part of a group
        if ((allCounts[v] || 0) > (boardCounts[v] || 0)) {
          // And this value actually forms a meaningful group (2+)
          if ((allCounts[v] || 0) >= 2) {
            holeCardContributes = true;
            break;
          }
        }
      }

      if (!holeCardContributes) {
        return false;
      }
    }

    // For flush: at least one hole card must be of the flush suit
    if (
      newHand.rank === HandRank.Flush ||
      newHand.rank === HandRank.StraightFlush ||
      newHand.rank === HandRank.RoyalFlush
    ) {
      const allCards = [...holeCards, ...boardCards];
      const suitCounts: Record<string, number> = {};
      for (const c of allCards)
        suitCounts[c.suit] = (suitCounts[c.suit] || 0) + 1;
      const flushSuit = Object.entries(suitCounts).find(
        ([_, count]) => count >= 5
      )?.[0];
      if (flushSuit && !holeSuits.includes(flushSuit as any)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Find all outs that improve the current hand.
 * Only works when communityCards.length is 3 or 4 (flop or turn).
 */
export function findOuts(
  holeCards: Card[],
  communityCards: Card[]
): OutsResult {
  const allCurrent = [...holeCards, ...communityCards];
  const currentHand = evaluateHand(allCurrent);
  const currentRank = currentHand.rank;

  const usedCards = [...holeCards, ...communityCards];
  const remaining = createDeck().filter(
    (c) => !usedCards.some((u) => isSameCard(u, c))
  );
  const cardsToCome = 5 - communityCards.length;

  // --- Single-card outs ---
  const improvementMap = new Map<HandRank, Card[]>();
  const improvingCards = new Set<string>();

  for (const card of remaining) {
    const withCard = [...allCurrent, card];
    const newHand = evaluateHand(withCard);
    if (newHand.rank > currentRank) {
      if (
        !improvementUsesHoleCards(
          holeCards,
          communityCards,
          card,
          newHand,
          currentHand
        )
      ) {
        continue;
      }
      const key = newHand.rank;
      if (!improvementMap.has(key)) improvementMap.set(key, []);
      improvementMap.get(key)!.push(card);
      improvingCards.add(cardKey(card));
    }
  }

  const outGroups: OutGroup[] = Array.from(improvementMap.entries())
    .map(([rank, cards]) => {
      const hitPercent = exactHitProbability(
        cards.length,
        remaining.length,
        cardsToCome
      );
      return {
        improvementName: HAND_NAMES[rank],
        improvementRank: rank,
        cards,
        count: cards.length,
        hitPercent,
      };
    })
    .sort((a, b) => b.improvementRank - a.improvementRank);

  const totalOuts = improvingCards.size;
  const hitAnyPercent = exactHitProbability(
    totalOuts,
    remaining.length,
    cardsToCome
  );

  // --- Runner-runner outs (flop only, 2 cards to come) ---
  // Find improvements that need BOTH cards working together
  // (i.e. neither card alone is an out for that rank)
  // Also count ALL improving combos (single-card + runner-runner) for total odds
  const runnerRunnerGroups: RunnerRunnerGroup[] = [];
  let totalImprovingCombos = 0;
  let totalPossibleCombos = 0;

  if (cardsToCome === 2) {
    const rrMap = new Map<HandRank, [Card, Card][]>();
    totalPossibleCombos = (remaining.length * (remaining.length - 1)) / 2;

    for (let i = 0; i < remaining.length; i++) {
      for (let j = i + 1; j < remaining.length; j++) {
        const c1 = remaining[i];
        const c2 = remaining[j];
        const withBoth = [...allCurrent, c1, c2];
        const newHand = evaluateHand(withBoth);

        if (newHand.rank > currentRank) {
          // Check the board alone (5 community cards) — if the board
          // achieves this rank, it helps everyone equally
          const boardCards = [...communityCards, c1, c2];
          const boardHand = evaluateHand(boardCards);
          if (boardHand.rank >= newHand.rank) {
            continue;
          }

          // This combo improves the hand using hole cards — count it for the total
          totalImprovingCombos++;

          // Check if EITHER card alone already achieves this rank
          // (if so, it's already covered by single-card outs, not runner-runner)
          const hand1 = evaluateHand([...allCurrent, c1]);
          const hand2 = evaluateHand([...allCurrent, c2]);
          if (hand1.rank >= newHand.rank || hand2.rank >= newHand.rank) {
            continue;
          }

          if (!rrMap.has(newHand.rank)) rrMap.set(newHand.rank, []);
          rrMap.get(newHand.rank)!.push([c1, c2]);
        }
      }
    }

    for (const [rank, combos] of rrMap.entries()) {
      const hitPercent = round1((combos.length / totalPossibleCombos) * 100);
      runnerRunnerGroups.push({
        improvementName: HAND_NAMES[rank],
        improvementRank: rank,
        combos,
        comboCount: combos.length,
        totalCombos: totalPossibleCombos,
        hitPercent,
      });
    }
    runnerRunnerGroups.sort((a, b) => b.improvementRank - a.improvementRank);
  }

  const hitAnyIncludingRRPercent =
    cardsToCome === 2 && totalPossibleCombos > 0
      ? round1((totalImprovingCombos / totalPossibleCombos) * 100)
      : hitAnyPercent;

  // Determine a meaningful current hand label
  let currentHandLabel = currentHand;
  if (communityCards.length >= 3) {
    const boardValues = communityCards.map((c) => RANK_VALUE[c.rank]);
    const holeValues = holeCards.map((c) => RANK_VALUE[c.rank]);
    const boardCounts: Record<number, number> = {};
    for (const v of boardValues) boardCounts[v] = (boardCounts[v] || 0) + 1;

    if (currentHand.rank === HandRank.Pair) {
      const pairValue = currentHand.kickers[0];
      if ((boardCounts[pairValue] || 0) >= 2) {
        currentHandLabel = {
          ...currentHand,
          name: `${currentHand.name} (board)`,
        };
      }
    }
    if (currentHand.rank === HandRank.TwoPair) {
      const pair1 = currentHand.kickers[0];
      const pair2 = currentHand.kickers[1];
      if ((boardCounts[pair1] || 0) >= 2 && (boardCounts[pair2] || 0) >= 2) {
        currentHandLabel = {
          ...currentHand,
          name: `${currentHand.name} (board)`,
        };
      }
    }
    if (currentHand.rank === HandRank.HighCard) {
      const topKicker = currentHand.kickers[0];
      if (!holeValues.includes(topKicker)) {
        currentHandLabel = {
          ...currentHand,
          name: `${currentHand.name} (board)`,
        };
      }
    }
  }

  return {
    currentHand: currentHandLabel,
    outGroups,
    runnerRunnerGroups,
    totalOuts,
    remainingCount: remaining.length,
    cardsToCome,
    hitAnyPercent,
    hitAnyIncludingRRPercent,
    totalImprovingCombos,
    totalPossibleCombos,
  };
}

function cardKey(card: Card): string {
  return card.rank + card.suit;
}

/**
 * Exact probability of hitting at least one out.
 * P = 1 - C(non_outs, cardsToCome) / C(remaining, cardsToCome)
 */
function exactHitProbability(
  outs: number,
  remaining: number,
  cardsToCome: number
): number {
  if (outs === 0 || cardsToCome === 0) return 0;
  const nonOuts = remaining - outs;

  if (cardsToCome === 1) {
    return round1((outs / remaining) * 100);
  }

  // 2 cards to come
  const pMiss = (nonOuts / remaining) * ((nonOuts - 1) / (remaining - 1));
  return round1((1 - pMiss) * 100);
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/**
 * Calculate exact hit probability for a specific out group.
 */
export function outGroupHitProbability(
  groupCount: number,
  remaining: number,
  cardsToCome: number
): number {
  return exactHitProbability(groupCount, remaining, cardsToCome);
}

// --- Equity vs Random Hand (Exhaustive) ---

export interface EquityResult {
  winPercent: number;
  tiePercent: number;
  losePercent: number;
  currentHand: string;
  totalMatchups: number;
}

/**
 * Exact equity vs. a random opponent hand.
 * Use only with 4 or 5 community cards for performance.
 */
export function calculateEquity(
  holeCards: Card[],
  communityCards: Card[]
): EquityResult {
  const usedCards = [...holeCards, ...communityCards];
  const remaining = createDeck().filter(
    (c) => !usedCards.some((u) => isSameCard(u, c))
  );
  const communityToFill = 5 - communityCards.length;

  let wins = 0;
  let ties = 0;
  let total = 0;

  if (communityToFill === 0) {
    // River: enumerate all opponent hands
    const myHand = evaluateHand([...holeCards, ...communityCards]);
    for (let i = 0; i < remaining.length; i++) {
      for (let j = i + 1; j < remaining.length; j++) {
        const oppHand = evaluateHand([
          remaining[i],
          remaining[j],
          ...communityCards,
        ]);
        const cmp = compareHands(myHand, oppHand);
        if (cmp > 0) wins++;
        else if (cmp === 0) ties++;
        total++;
      }
    }
  } else if (communityToFill === 1) {
    // Turn: enumerate all remaining community cards × opponent hands
    for (let c = 0; c < remaining.length; c++) {
      const fullCommunity = [...communityCards, remaining[c]];
      const leftover = remaining.filter((_, idx) => idx !== c);
      const myHand = evaluateHand([...holeCards, ...fullCommunity]);
      for (let i = 0; i < leftover.length; i++) {
        for (let j = i + 1; j < leftover.length; j++) {
          const oppHand = evaluateHand([
            leftover[i],
            leftover[j],
            ...fullCommunity,
          ]);
          const cmp = compareHands(myHand, oppHand);
          if (cmp > 0) wins++;
          else if (cmp === 0) ties++;
          total++;
        }
      }
    }
  }

  const currentHand =
    communityCards.length >= 3
      ? evaluateHand([...holeCards, ...communityCards]).name
      : "—";

  return {
    winPercent: total > 0 ? round1((wins / total) * 100) : 0,
    tiePercent: total > 0 ? round1((ties / total) * 100) : 0,
    losePercent: total > 0 ? round1(((total - wins - ties) / total) * 100) : 0,
    currentHand,
    totalMatchups: total,
  };
}

// --- Utility: format a card for display ---

export function formatCard(card: Card): string {
  return RANK_NAMES[card.rank] + SUIT_SYMBOLS[card.suit];
}

/**
 * Determine the "effective" hand name — marks hands that are entirely
 * provided by the board (e.g. board pair) so the player knows it's not
 * specifically their hand.
 */
export function effectiveHandName(
  holeCards: Card[],
  communityCards: Card[]
): string {
  const fullHand = evaluateHand([...holeCards, ...communityCards]);
  if (communityCards.length >= 5) {
    const boardHand = evaluateHand(communityCards);
    if (compareHands(fullHand, boardHand) <= 0) {
      return `${fullHand.name} (board)`;
    }
  }
  return fullHand.name;
}
